import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, Alert } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { useAtomValue, useSetAtom } from 'jotai';
import { illustrationInputAtom, illustrationResultAtom } from './illustrationAtoms';
import { Icon } from '../components/Icon';
import { EditModal } from '../components/EditModal';
import { ShareModal } from '../components/ShareModal';
import { PdfExportModal, PdfExportType } from '../components/PdfExportModal';
import { SolutionLibraryModal, Solution } from '../components/SolutionLibraryModal';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { calculateULCashflow, getTotalRiderPremium, calculateInsuranceAge } from '../core/calculations';
import { RIDERS, R5_MODES } from '../data/riders';
import { PRODUCTS } from '../data/products';
import { generateUUID } from '../core/uuid';

import { useEditModals, buildFieldsFor, getModalTitle, EditModalId } from '../data/editModals';
import { M07FinancialSummary } from '../components/M07FinancialSummary';
import { M07FeeOverview } from '../components/M07FeeOverview';
import { M07EstimationTable } from '../components/M07EstimationTable';
import { M07ProductOverview } from '../components/M07ProductOverview';
import { M07CustomerCard } from '../components/M07CustomerCard';
import { M07TotalPremiumCard } from '../components/M07TotalPremiumCard';
import { M07AttachedProductsCard } from '../components/M07AttachedProductsCard';
import { Toast, useToast } from '../components/Toast';
import { M07EposView } from '../components/m07/M07EposView';
import { M07FlowerView } from '../components/m07/M07FlowerView';
import { M07DetailsView } from '../components/m07/M07DetailsView';
import { saveIllustrationLocal } from '../data/savedIllustrations';
import { illustrationRepository } from '../repositories';

type NavigationProp = any; // StackNavigationProp

// Flat-shape list (kept for backward compatibility with existing UI rendering)
const RIDERS_DATA = RIDERS.map((r) => ({
  id: r.id,
  name: r.name,
  price: r.basePrice,
  desc: r.shortDesc,
  color: r.color,
  category: r.category,
}));

const PETAL_DETAILS = [
  {
    id: 'R1',
    name: 'Sống Khỏe Mỗi Ngày',
    color: '#E11D48',
    icon: 'favorite',
    bullets: [
      'Hạn mức bảo vệ y tế lên đến 1 Tỷ VNĐ/năm.',
      'Chi trả 100% chi phí điều trị nội trú, phẫu thuật thực tế tại bệnh viện.',
      'Bảo lãnh viện phí 24/7 tại hệ thống hơn 300 bệnh viện chất lượng cao trong nước.',
    ]
  },
  {
    id: 'R2',
    name: 'Lá Chắn Xanh',
    color: '#D97706',
    icon: 'coronavirus',
    bullets: [
      'Bảo vệ tài chính trước 134 Bệnh lý nghiêm trọng qua nhiều giai đoạn.',
      'Chi trả tối đa lên đến 325% Số tiền bảo hiểm.',
      'Hỗ trợ tài chính khẩn cấp giúp khách hàng an tâm tiếp cận các liệu trình điều trị tiên tiến.'
    ]
  },
  {
    id: 'R3',
    name: 'Dự Phòng Xanh',
    color: '#00A651',
    icon: 'payments',
    bullets: [
      'Miễn đóng toàn bộ phí bảo hiểm còn lại của hợp đồng khi Người mua xảy ra biến cố sức khỏe lớn.',
      'Đảm bảo hợp đồng bảo hiểm chính và các quyền lợi bổ trợ khác tiếp tục duy trì hiệu lực.',
      'Đảm bảo tương lai học vấn của con cái và gia đình không bị ảnh hưởng đứt quãng.'
    ]
  },
  {
    id: 'R4',
    name: 'Hộ Vệ Xanh',
    color: '#2563EB',
    icon: 'airline_seat_flat',
    bullets: [
      'Chi trả lên tới 300% số tiền bảo hiểm đối với thương tật do tai nạn.',
      'Bảo vệ 24/7 phạm vi toàn cầu bao gồm tai nạn giao thông, sinh hoạt, thể thao, lao động.',
      'Hỗ trợ chi phí nằm viện nội trú đặc biệt do tai nạn.'
    ]
  },
  {
    id: 'R5',
    name: 'Bảo Thân Xanh',
    color: '#7C3AED',
    icon: 'local_hospital',
    bullets: [
      'Trợ cấp tiền mặt từ 500.000đ đến 2.000.000đ/ngày nằm viện thực tế để bù đắp thu nhập.',
      'Nhân đôi mức trợ cấp khi nằm viện điều trị tại Khoa chăm sóc đặc biệt (ICU).',
      'Thủ tục thanh toán đơn giản, nhanh chóng qua ứng dụng di động.'
    ]
  }
];

// Product config helper — BV min/max, max age
const XUM_CONFIG = PRODUCTS.find((p) => p.id === 'XUM')?.config;

export const M07ResultScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProp<RootStackParamList, 'M07ResultScreen'>>();
  const illustrationInput = useAtomValue(illustrationInputAtom);
  const setIllustrationResult = useSetAtom(illustrationResultAtom);
  const params = route.params || {};

  const customerName = params.customerName || illustrationInput.customerName || 'Nguyễn Văn Minh';
  const customerAge = params.customerAge || illustrationInput.customerAge || 35;
  const customerGender = params.customerGender || illustrationInput.customerGender || 'Nam';
  const freq = params.freq || illustrationInput.freq || 'Hàng năm';

  // ── Editable state — khởi tạo từ Jotai Atom (ưu tiên) hoặc route.params (fallback) ──
  const [sumAssured, setSumAssured] = useState<string>(params.sumAssured || illustrationInput.sumAssured || '1.000.000.000');
  const [term, setTerm] = useState<string>(params.term || illustrationInput.term || '20 năm');
  const [estPremium, setEstPremium] = useState<string>(params.estPremium || illustrationInput.estPremium || '15.209.000');
  const [selectedRiders, setSelectedRiders] = useState<string[]>(params.selectedRiders || illustrationInput.selectedRiders || []);
  // Top-up (Đầu tư thêm) — defaults to 0
  const [topUpAmount, setTopUpAmount] = useState<number>(params.topUpAmount ?? illustrationInput.topUpAmount ?? 0);
  const [topUpStartYear, setTopUpStartYear] = useState<number>(params.topUpStartYear ?? illustrationInput.topUpStartYear ?? 1);
  const [topUpDuration, setTopUpDuration] = useState<number>(params.topUpDuration ?? illustrationInput.topUpDuration ?? 0);
  // R5 Bảo Thân Xanh mode — defaults to complimentary (0 đ)
  const [r5Mode, setR5Mode] = useState<'complimentary' | 'attached'>(params.r5Mode ?? illustrationInput.r5Mode ?? 'complimentary');


  // Custom configurations from M06
  const [r1Package, setR1Package] = useState<'silver' | 'gold' | 'platinum'>(
    params.r1Package || 'silver',
  );
  const [r2LimitCuoi, setR2LimitCuoi] = useState<number>(params.r2LimitCuoi ?? 100_000_000);
  const [r2LimitSom, setR2LimitSom] = useState<number>(params.r2LimitSom ?? 0);
  const [r2LimitDacBiet, setR2LimitDacBiet] = useState<number>(params.r2LimitDacBiet ?? 0);
  const [r3DailyAllowance, setR3DailyAllowance] = useState<number>(params.r3DailyAllowance ?? 1_000_000);
  const [r4PhysicalLimit, setR4PhysicalLimit] = useState<number>(params.r4PhysicalLimit ?? 100_000_000);
  const [r5SumAssured, setR5SumAssured] = useState<number>(
    params.r5SumAssured ?? (r5Mode === 'attached' ? 950_000_000 : 750_000_000),
  );
  const [r5Term, setR5Term] = useState<'20_years' | 'whole_life'>(params.r5Term || '20_years');
  const illustrationId = params.illustrationId;

  useEffect(() => {
    if (illustrationId) {
      illustrationRepository.getById(illustrationId).then((record) => {
        if (record) {
          if (record.sumAssured) setSumAssured(record.sumAssured);
          if (record.term) setTerm(String(record.term) + ' năm');
          if (record.premium) setEstPremium(record.premium);
          if (record.selectedRiders) setSelectedRiders(record.selectedRiders);
          if (record.topUpAmount !== undefined) setTopUpAmount(record.topUpAmount);
        }
      });
    }
  }, [illustrationId]);

  // Auto-sync editable parameters back to IndexedDB draft
  useEffect(() => {
    if (illustrationId) {
      illustrationRepository.update(illustrationId, {
        sumAssured,
        term: parseInt(term, 10) || 20,
        premium: estPremium,
        selectedRiders,
        topUpAmount,
        lastUpdated: new Date().toLocaleDateString('vi-VN'),
      });
    }
  }, [illustrationId, sumAssured, term, estPremium, selectedRiders, topUpAmount]);

  // Derived: total top-up across active years
  const totalTopUp = topUpAmount * Math.max(0, topUpDuration);

  // Derived: riderPremiumOverrides — phụ thuộc r5Mode + các override state
  const riderPremiumOverrides = useMemo<Record<string, number> | undefined>(() => {
    const overrides: Record<string, number> = {};
    if (r5Mode === 'attached') overrides.R5 = 796_000;
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  }, [r5Mode]);

  const [activeTableTab, setActiveTableTab] = useState<'overview' | 'estimation' | 'overview_products'>('overview');
  const [showFullTable, setShowFullTable] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<'high' | 'low'>('high');
  const [viewMode, setViewMode] = useState<'epos' | 'details' | 'flower'>('epos');
  const [activeChartTab, setActiveChartTab] = useState<'chart' | 'table'>('table');
  const [selectedPetal, setSelectedPetal] = useState<number>(0);

  const parseNumber = (str: string): number => {
    const cleanStr = str.replace(/\./g, '').replace(/,/g, '');
    const parsed = parseInt(cleanStr, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Edit modal state — see data/editModals.ts for field definitions
  const editModals = useEditModals({
    plan: 'Cơ bản',
    sumAssured: sumAssured.toString(),
    term: term.replace(/\D/g, '') || '20',
    r1Package: 'Bạc',
    r1Include: 'true',
    r2MainAmount: '100000000',
    r2IncludeCuoi: 'true',
    r2SomAmount: '0',
    r2DacBietAmount: '0',
    r3Amount: '1000000',
    r4Amount: '100000000',
    r5Package: '20 Năm',
    r5SumAssured: '200000000',
  });

  // Share / PDF / Library modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  // Toast for save/error feedback (Alert.alert is no-op on react-native-web!)
  const { toastProps, showToast, hideToast } = useToast();

  // Mock library solutions (would be loaded from AsyncStorage)
  const [library] = useState<Solution[]>([
    {
      id: 'sol-1',
      name: 'Chọn Sản Phẩm Chính',
      productId: 'XUM',
      premium: 0,
      status: 'scheduled',
      createdAt: '2026-06-23T10:30:00Z',
    },
    {
      id: 'sol-2',
      name: 'Xanh Ước Mơ',
      productId: 'XUM',
      premium: 14_729_000,
      status: 'saved',
      createdAt: '2026-06-23T15:45:00Z',
    },
    {
      id: 'sol-3',
      name: 'XUM Nâng cao - KH 35t',
      productId: 'XUM',
      premium: 18_500_000,
      status: 'saved',
      createdAt: '2026-06-20T09:15:00Z',
    },
  ]);

  const handleShareClick = () => {
    // Build share URL (reuse existing logic from handleShare)
    try {
      const dataObj = {
        name: customerName,
        age: customerAge,
        gender: customerGender,
        sa: sumAssured,
        term: term,
        freq: freq,
        prem: estPremium,
        riders: selectedRiders,
        topUpAmount,
        topUpStartYear,
        topUpDuration,
        r5Mode,
      };
      const jsonStr = JSON.stringify(dataObj);
      const encoded = btoa(unescape(encodeURIComponent(jsonStr)));
      const url = (typeof window !== 'undefined' ? window.location.origin : 'https://equote.app') + '/share?data=' + encoded;
      setShareUrl(url);
      setShareModalOpen(true);
    } catch (e) {
      showToast('Không thể tạo liên kết chia sẻ', 'error');
    }
  };

  const handlePdfExport = (type: PdfExportType) => {
    // Real implementation would call /api/pdf/export with type
    // For now: navigate to M09PdfScreen which has the PDF preview
    navigation.navigate('M09PdfScreen', { illustrationId: illustrationId || generateUUID() });
  };

  const handleSaveLocal = () => {
    saveIllustrationLocal({
      proposalName: 'Bảo hiểm Liên kết chung Xanh Ước Mơ',
      customerName,
      customerAge: typeof customerAge === 'number' ? customerAge : parseInt(customerAge, 10) || 35,
      customerGender: customerGender as any,
      sumAssured,
      premium: formatNumber(totalPremiumNum),
      freq,
      term,
      selectedRiders,
      topUpAmount,
      topUpStartYear,
      topUpDuration,
      r5Mode,
    });
    showToast('Đã lưu bản minh họa vào danh sách local thành công!', 'success');
  };

  const totalPremiumNum = parseNumber(estPremium);
  const dailyPremiumNum = Math.round(totalPremiumNum / 365);

  // Use Year-1 rider pricing for display & basePremium calculation
  const parsedAge = typeof customerAge === 'number' ? customerAge : parseInt(customerAge, 10) || 35;
  const ridersTotal = getTotalRiderPremium(selectedRiders, 1, riderPremiumOverrides);
  const basePremium = Math.max(0, totalPremiumNum - ridersTotal);

  // Update edit modal defaults with computed basePremium / topUp
  // (must be in useEffect to avoid "Too many re-renders" — setValue triggers re-render)
  useEffect(() => {
    editModals.setValue('basePremium', formatNumber(basePremium));
    editModals.setValue('topUpAmount', formatNumber(topUpAmount));
    editModals.setValue('topUpStartYear', topUpStartYear.toString());
    editModals.setValue('topUpDuration', topUpDuration.toString());
  }, [basePremium, topUpAmount, topUpStartYear, topUpDuration]);

  // Call cashflow calculation engine dynamically for both scenarios
  const selectedProductId = (route.params as any)?.productId || 'XUM';
  const isILPProduct = selectedProductId === 'XPQ' || selectedProductId === 'XTL' || selectedProductId === 'XTL-PS';
  const highRate = isILPProduct ? 0.07 : 0.04;
  const customerDob = (params as any).customerDob || illustrationInput.customerDob || '25/11/1996';
  const insuranceAge = calculateInsuranceAge(customerDob);

  const cashflowInput = {
    productId: selectedProductId,
    age: insuranceAge,
    gender: (customerGender === 'Nữ' ? 'Nữ' : 'Nam') as 'Nam' | 'Nữ',
    sumAssured: parseNumber(sumAssured),
    totalPremium: totalPremiumNum,
    term: parseInt(term.toString().replace(/[^0-9]/g, ''), 10) || 20,
    selectedRiderIds: selectedRiders,
    topUpAmount,
    topUpStartYear,
    topUpDuration,
    riderPremiumOverrides,
  };

  const cashflowHigh = calculateULCashflow({ ...cashflowInput, declaredInterestRate: highRate });
  const cashflowLow = calculateULCashflow({ ...cashflowInput, declaredInterestRate: 0.005 });

  const cashflow = selectedScenario === 'high' ? cashflowHigh : cashflowLow;

  useEffect(() => {
    if (cashflow && cashflow.length > 0) {
      setIllustrationResult(cashflow);
    }
  }, [cashflow, setIllustrationResult]);

  const year20RowHigh = cashflowHigh.find(r => r.year === 20);

  const year20RowLow = cashflowLow.find(r => r.year === 20);

  const surrender20HighText = year20RowHigh ? `${formatNumber(year20RowHigh.surrenderValue)} đ` : '0 đ';
  const surrender20LowText = year20RowLow ? `${formatNumber(year20RowLow.surrenderValue)} đ` : '0 đ';

  const isRiderSelected = (riderId: string): boolean => {
    return selectedRiders.includes(riderId);
  };

  const handleShare = () => {
    try {
      const dataObj = {
        name: customerName,
        age: customerAge,
        gender: customerGender,
        sa: sumAssured,
        term: term,
        freq: freq,
        prem: estPremium,
        riders: selectedRiders,
        topUpAmount,
        topUpStartYear,
        topUpDuration,
        r5Mode,
        // Custom configurations
        r1Package,
        r2LimitCuoi,
        r2LimitSom,
        r2LimitDacBiet,
        r3DailyAllowance,
        r4PhysicalLimit,
        r5SumAssured,
        r5Term,
        riderPremiumOverrides,
      };

      const jsonStr = JSON.stringify(dataObj);
      const encoded = btoa(unescape(encodeURIComponent(jsonStr)));

      const shareUrl = window.location.origin + '/share?data=' + encoded;

      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(shareUrl);
        showToast('Liên kết minh họa đã được sao chép vào bộ nhớ tạm', 'success');
      } else {
        showToast(`Liên kết chia sẻ: ${shareUrl}`, 'info');
      }
    } catch (e) {
      showToast('Không thể tạo liên kết chia sẻ', 'error');
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* Top Header Bar: Left Action Buttons + Right Pill Tabs */}
        <View style={styles.topHeaderBar}>
          <View style={styles.topLeftActionsGroup}>
            <TouchableOpacity
              style={styles.headerBtnEdit}
              onPress={() => navigation.navigate('M06ConfigureProductScreen')}
            >
              <Icon name="edit" size={14} color="#00A651" />
              <Text style={styles.headerBtnEditText}>Chỉnh sửa TLMHBH</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.headerBtnSave}
              onPress={handleSaveLocal}
            >
              <Icon name="save" size={14} color="#059669" />
              <Text style={styles.headerBtnSaveText}>Lưu TLMHBH</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.headerBtnPdf}
              onPress={() => handlePdfExport('cashflow')}
            >
              <Icon name="picture_as_pdf" size={14} color="#dc2626" />
              <Text style={styles.headerBtnPdfText}>Xem trước TLMHBH</Text>
            </TouchableOpacity>
          </View>

          {/* Right-aligned 3 Pill Tabs */}
          <View style={styles.viewModeContainer}>
            <TouchableOpacity
              style={[styles.viewModeBtn, viewMode === 'epos' && styles.viewModeBtnActive]}
              onPress={() => setViewMode('epos')}
            >
              <Icon name="table_chart" size={14} color={viewMode === 'epos' ? '#ffffff' : colors.onSurfaceVariant} />
              <Text style={[styles.viewModeBtnText, viewMode === 'epos' && styles.viewModeBtnTextActive]}>
                Cơ bản
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.viewModeBtn, viewMode === 'details' && styles.viewModeBtnActive]}
              onPress={() => setViewMode('details')}
            >
              <Icon name="description" size={14} color={viewMode === 'details' ? '#ffffff' : colors.onSurfaceVariant} />
              <Text style={[styles.viewModeBtnText, viewMode === 'details' && styles.viewModeBtnTextActive]}>
                Chi tiết
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.viewModeBtn, viewMode === 'flower' && styles.viewModeBtnActive]}
              onPress={() => setViewMode('flower')}
            >
              <Icon name="local_florist" size={14} color={viewMode === 'flower' ? '#ffffff' : colors.onSurfaceVariant} />
              <Text style={[styles.viewModeBtnText, viewMode === 'flower' && styles.viewModeBtnTextActive]}>
                Bông hoa
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* View Mode Component Switching */}
        {viewMode === 'epos' && (
          <M07EposView
            cashflowHigh={cashflowHigh}
            cashflowLow={cashflowLow}
            totalPremiumNum={totalPremiumNum}
            sumAssured={sumAssured}
            formatNumber={formatNumber}
            parseNumber={parseNumber}
          />
        )}

        {viewMode === 'flower' && (
          <M07FlowerView
            customerName={customerName}
            customerAge={String(customerAge)}
            totalPremiumNum={totalPremiumNum}
            formatNumber={formatNumber}
            r1Package={r1Package}
            r2LimitCuoi={r2LimitCuoi}
            r3DailyAllowance={r3DailyAllowance}
            r4PhysicalLimit={r4PhysicalLimit}
            r5Mode={r5Mode as any}
            r5SumAssured={r5SumAssured}
            selectedRiders={selectedRiders}
          />
        )}

        {viewMode === 'details' && (
          <M07DetailsView
            basePremium={basePremium}
            sumAssured={sumAssured}
            surrender20LowText={surrender20LowText}
            surrender20HighText={surrender20HighText}
            term={term}
            activeTableTab={activeTableTab}
            setActiveTableTab={setActiveTableTab}
            ridersTotal={ridersTotal}
            totalPremiumNum={totalPremiumNum}
            topUpAmount={topUpAmount}
            totalTopUp={totalTopUp}
            cashflowHigh={cashflowHigh}
            cashflow={cashflow}
            selectedScenario={selectedScenario}
            setSelectedScenario={setSelectedScenario}
            showFullTable={showFullTable}
            setShowFullTable={setShowFullTable}
            selectedRiders={selectedRiders}
            r5Mode={r5Mode as any}
            r1Package={r1Package}
            r2LimitCuoi={r2LimitCuoi}
            r3DailyAllowance={r3DailyAllowance}
            r4PhysicalLimit={r4PhysicalLimit}
            r5SumAssured={r5SumAssured}
            r5Term={r5Term}
            riderPremiumOverrides={riderPremiumOverrides}
          />
        )}



        {/* Disclaimer Block */}
        <View style={styles.disclaimerCard}>
          <Icon name="gavel" size={16} color="#64748b" />
          <Text style={styles.disclaimerText}>
            <Text style={{ fontWeight: 'bold' }}>Tuyên bố miễn trừ trách nhiệm (Disclaimer): </Text>
            Tài liệu này được tạo ra bởi ứng dụng eQuote (công cụ hỗ trợ tính nhanh không chính thức dành cho FA) chỉ nhằm mục đích tham khảo. Các thông số, số liệu về phí đóng, dòng tiền và quyền lợi chỉ mang tính minh họa giả định tại thời điểm lập và không thay thế cho Bản minh họa sản phẩm chính thức do Tổng công ty Manulife Việt Nam phát hành.
          </Text>
        </View>
      </ScrollView>

      {/* Toast (snackbar) */}
      <Toast
        visible={toastProps.visible}
        message={toastProps.message}
        variant={toastProps.variant}
        onHide={hideToast}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  topHeaderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[1.5],
    flexWrap: 'wrap',
    gap: spacing[1.5],
  },
  topLeftActionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    flexWrap: 'wrap',
  },
  headerBtnEdit: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#00A651',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 14,
  },
  headerBtnEditText: {
    fontSize: 11.5,
    fontWeight: fontWeights.semibold,
    color: '#00A651',
  },
  headerBtnSave: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#059669',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 14,
  },
  headerBtnSaveText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#059669',
  },
  headerBtnPdf: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#dc2626',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  headerBtnPdfText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#dc2626',
  },
  headerBtnShare: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#2563eb',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  headerBtnShareText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#2563eb',
  },
  breadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  hamburgerBtn: {
    padding: spacing[1],
    marginRight: spacing[1],
  },
  bhBadge: {
    backgroundColor: '#059669',
    width: 26,
    height: 26,
    borderRadius: 13,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bhBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  breadLink: {
    fontSize: 13,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  breadActive: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: fontWeights.semibold,
  },
  headerActionsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
  },
  headerActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
  },
  headerActionText: {
    fontSize: 13,
    color: '#334155',
    fontWeight: fontWeights.medium,
  },
  eposTabBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingHorizontal: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    marginBottom: spacing[3],
  },
  eposTabLeftRow: {
    flexDirection: 'row',
    gap: spacing[6],
  },
  eposTabItem: {
    paddingVertical: spacing[3],
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  eposTabItemActive: {
    borderBottomColor: '#059669',
  },
  eposTabText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  eposTabTextActive: {
    color: '#0f172a',
    fontWeight: fontWeights.bold,
  },
  eposTabRightControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  eposControlPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    backgroundColor: '#f1f5f9',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  eposControlText: {
    fontSize: 12,
    color: '#334155',
    fontWeight: fontWeights.medium,
  },
  infoIconBtn: {
    padding: spacing[1],
  },
  unitText: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  tableCardFull: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    marginHorizontal: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden',
  },
  thRow: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[2],
  },
  thCell: {
    fontSize: 11.5,
    fontWeight: fontWeights.bold,
    color: '#334155',
    paddingHorizontal: spacing[1.5],
  },
  trRow: {
    flexDirection: 'row',
    paddingVertical: spacing[2.5],
    paddingHorizontal: spacing[2],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  trRowAlt: {
    backgroundColor: '#fafafa',
  },
  tdCell: {
    fontSize: 12,
    color: '#1e293b',
    paddingHorizontal: spacing[1.5],
  },
  fixedBottomFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 64,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingHorizontal: spacing[4],
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 12,
    zIndex: 9999,
  },
  footerBtnEdit: {
    borderWidth: 1.5,
    borderColor: '#cbd5e1',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    backgroundColor: '#ffffff',
  },
  footerBtnEditText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#334155',
  },
  footerRightGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  footerBtnSave: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    borderWidth: 1.5,
    borderColor: '#86efac',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2.5],
    backgroundColor: '#f0fdf4',
  },
  footerBtnSaveText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#059669',
  },
  footerBtnSummary: {
    borderWidth: 1.5,
    borderColor: '#fca5a5',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    backgroundColor: '#ffffff',
  },
  footerBtnSummaryText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#dc2626',
  },
  footerBtnFullPrimary: {
    backgroundColor: '#e53e3e',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[2.5],
  },
  footerBtnFullPrimaryText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  viewModeRowRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: spacing[2],
  },
  viewModeContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 2,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignSelf: 'flex-end',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    gap: 2,
  },
  viewModeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 14,
    gap: 4,
  },
  viewModeBtnActive: {
    backgroundColor: colors.primary,
  },
  viewModeBtnText: {
    fontSize: 11.5,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  viewModeBtnTextActive: {
    color: '#ffffff',
  },

  /* EPOS STYLES */
  eposContainer: {
    gap: spacing[4],
  },
  eposHeaderCard: {
    backgroundColor: '#002244',
    padding: spacing[4],
    borderRadius: borderRadius.md,
    position: 'relative',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  eposHeaderOverlay: {
    position: 'absolute',
    right: -20,
    bottom: -20,
    opacity: 0.2,
  },
  eposShieldIcon: {
    transform: [{ rotate: '-10deg' }],
  },
  eposHeaderContent: {
    zIndex: 1,
  },
  eposTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  eposClientName: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  eposClientInfo: {
    fontSize: 13,
    color: '#94a3b8',
    marginTop: 2,
  },
  eposStatusBadge: {
    backgroundColor: '#00A651',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  eposStatusBadgeText: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  eposProductTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    marginTop: spacing[4],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: borderRadius.sm,
  },
  eposProductTagText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    color: '#a8f8cc',
  },
  eposSummaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[3],
  },
  eposSummaryCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: '#ffffff',
    padding: spacing[3],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderLeftWidth: 4,
  },
  eposCardLabel: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    color: '#64748b',
    letterSpacing: 0.5,
  },
  eposCardValue: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: spacing[1],
  },
  eposCardUnit: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 1,
  },
  chartCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing[2],
    marginBottom: spacing[3],
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  chartLegends: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    color: '#64748b',
  },
  svgChartContainer: {
    width: '100%',
    height: 180,
    marginTop: spacing[2],
  },
  xAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 40,
    paddingRight: 10,
    marginTop: spacing[1],
  },
  xAxisLabel: {
    fontSize: 9,
    color: '#64748b',
  },
  fallbackChartBox: {
    height: 150,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  fallbackChartText: {
    fontSize: 12,
    color: '#64748b',
    fontStyle: 'italic',
  },
  eposTableCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden',
  },
  eposTableHeaderBtn: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing[4],
  },
  eposTableHeaderTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  eposTableHead: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  eposTableHeadCell: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    color: '#475569',
  },
  eposTableRow: {
    flexDirection: 'row',
    paddingVertical: spacing[2.5],
    paddingHorizontal: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  eposTableCell: {
    fontSize: 11.5,
    color: '#334155',
  },

  /* FLOWER MODE STYLES */
  flowerContainer: {
    gap: spacing[4],
  },
  flowerHeaderStats: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: spacing[3],
    gap: spacing[2],
  },
  flowerStatBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flowerStatLabel: {
    fontSize: 9,
    color: '#64748b',
    textAlign: 'center',
  },
  flowerStatValue: {
    fontSize: 13.5,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: 2,
    textAlign: 'center',
  },
  flowerStatSub: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 1,
    textAlign: 'center',
  },
  flowerCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
  },
  flowerCardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    textAlign: 'center',
  },
  flowerCardSub: {
    fontSize: 11,
    color: '#64748b',
    textAlign: 'center',
    marginTop: 2,
    marginBottom: spacing[4],
  },
  flowerSvgWrapper: {
    width: 240,
    height: 240,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flowerFallback: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  petalDetailCard: {
    backgroundColor: '#f8fafc',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderLeftWidth: 4,
    width: '100%',
    marginTop: spacing[4],
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 40,
    gap: spacing[4],
  },
  clientCard: {
    backgroundColor: '#ffffff',
    padding: spacing[4],
    borderRadius: borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  clientIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clientName: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  clientProduct: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  dailyBadge: {
    alignItems: 'flex-end',
    backgroundColor: 'rgba(0, 166, 81, 0.08)',
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[3],
    borderRadius: borderRadius.md,
  },
  dailyBadgeLabel: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    color: '#00A651',
    textTransform: 'uppercase',
  },
  dailyBadgeValue: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#00A651',
    marginTop: 2,
  },

  /* TAB BAR NAVIGATION */
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    paddingVertical: spacing[2],
    borderRadius: borderRadius.sm,
  },
  tabButtonActive: {
    backgroundColor: '#EDF1F5',
  },
  tabButtonText: {
    fontSize: 12.5,
    fontWeight: fontWeights.medium,
    color: '#64748b',
  },
  tabButtonActiveText: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },

  tabContent: {
    gap: spacing[4],
  },

  /* FLOWER SVG CONTAINER */
  flowerWrapper: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    alignItems: 'center',
  },
  flowerTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  flowerSubtitle: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
    marginBottom: spacing[3],
  },
  svgContainer: {
    width: 300,
    height: 300,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeFlowerFallback: {
    height: 200,
    justifyContent: 'center',
  },
  petalSelectorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing[2],
    marginTop: spacing[4],
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.outlineVariant,
    paddingTop: spacing[3],
    width: '100%',
  },
  petalSelectorPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: colors.outline,
    backgroundColor: '#ffffff',
  },
  pillColorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  petalSelectorPillText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },

  /* PETAL DETAIL CARD */
  petalDetailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  editIconBtn: {
    padding: spacing[1.5],
    marginLeft: 'auto',
  },
  bottomActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[3],
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
    gap: spacing[2],
  },
  bottomActionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[1.5],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.md,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: colors.outline,
  },
  bottomActionText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.primary,
  },
  petalDetailTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    flex: 1,
  },
  statusBadge: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
  },
  petalDetailDesc: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[3],
    fontStyle: 'italic',
  },
  petalDetailBullets: {
    gap: spacing[2],
    marginBottom: spacing[3],
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
  },
  bulletText: {
    flex: 1,
    fontSize: 13,
    color: colors.onSurface,
    lineHeight: 18,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#fef3c7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    color: '#b45309',
    lineHeight: 17,
  },
  successBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#dcfce7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
  },
  successText: {
    flex: 1,
    fontSize: 12,
    color: '#15803d',
    lineHeight: 17,
  },

  /* ORIGINAL STYLES */
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  actionBtnOutline: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
    backgroundColor: '#ffffff',
  },
  actionBtnOutlineFull: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
    backgroundColor: '#ffffff',
  },
  actionBtnTextOutline: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  actionBtnSolid: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
  },
  actionBtnTextSolid: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },

  /* DETAILED BENEFITS TAB */
  sectionHeaderLine: {
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
    paddingBottom: spacing[2],
    marginBottom: spacing[3],
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  benefitDetailBox: {
    gap: spacing[2],
  },
  benefitDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  benefitDetailLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  benefitDetailValue: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  noRidersText: {
    fontSize: 13,
    color: '#64748b',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: spacing[4],
  },
  riderBenefitItem: {
    backgroundColor: colors.surface,
    padding: spacing[3],
    borderRadius: borderRadius.DEFAULT,
    borderLeftWidth: 4,
    gap: spacing[1],
  },
  riderBenefitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  riderBenefitName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
  riderBenefitPrice: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  riderBenefitSub: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },
  riderBenefitBullets: {
    marginTop: spacing[2],
    gap: spacing[1],
  },
  riderBulletPoint: {
    fontSize: 12,
    color: colors.onSurface,
    lineHeight: 16,
  },

  /* DISCLAIMER CARD STYLES */
  disclaimerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
    backgroundColor: 'rgba(100, 116, 139, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(100, 116, 139, 0.1)',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    marginTop: spacing[2],
  },
  disclaimerText: {
    flex: 1,
    fontSize: 11,
    color: '#64748b',
    lineHeight: 16,
    textAlign: 'justify',
  },
  /* Detailed Table Styles */
  tableTabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#cbd5e1',
    marginBottom: spacing[4],
  },
  tableTabBtn: {
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[4],
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginBottom: -2,
  },
  tableTabBtnActive: {
    borderBottomColor: '#00A651',
  },
  tableTabBtnText: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  tableTabBtnTextActive: {
    color: '#00A651',
    fontWeight: fontWeights.bold,
  },

  // ── Product info row (matches Curex top section) ──
  productInfoRow: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  productInfoCard: {
    flex: 1.6,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2],
  },
  productInfoBadge: {
    backgroundColor: '#DCFCE7',
    borderRadius: 4,
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    alignSelf: 'flex-start',
  },
  productInfoBadgeText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#00A651',
    letterSpacing: 0.5,
  },
  productInfoName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#003366',
  },
  productPlanTag: {
    backgroundColor: '#FEF3C7',
    borderRadius: 4,
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    alignSelf: 'flex-start',
  },
  productPlanTagText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#D97706',
  },
  productKeyMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
    marginTop: spacing[1],
  },
  productKeyMetricItem: {
    flex: 1,
    minWidth: 80,
  },
  productKeyMetricValue: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  productKeyMetricLabel: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 1,
  },
  investMoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#f0fdf4',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[2],
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  investMoreLabel: {
    flex: 1,
    fontSize: 12,
    color: '#374151',
  },
  investMoreValue: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#003366',
  },
  saRangeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  saRangeItem: {},
  saRangeGreen: {
    fontSize: 10,
    color: '#00A651',
  },
  saRangeRed: {
    fontSize: 10,
    color: '#EF4444',
  },

  // ── Fund allocation donut card ──
  fundAllocCard: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    alignItems: 'center',
    gap: spacing[2],
  },
  // Full-width variant for row 1 layout
  fundAllocCardFull: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[3],
  },
  fundAllocHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
  },
  // Row layout for 2 cards side-by-side
  cardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[4],
  },
  fundAllocTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#003366',
    textAlign: 'center',
  },
  fundAllocSub: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
  },
  donutContainer: {
    width: 110,
    height: 110,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fundLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  fundLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#00A651',
  },
  fundLegendText: {
    fontSize: 10,
    color: '#374151',
  },
});

